import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Camera, X, Plus, Loader2, Trash2, ImageIcon, UserCircle2, Upload } from "lucide-react";
import api, { fileUrl, formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import Avatar from "../Avatar";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../../lib/bookingStatus";
import { deDateLong } from "../../lib/dateFormat";

const KIND_LABEL = {
  arrival: "Anreise",
  departure: "Abreise",
  maintenance: "Instandhaltung",
  note: "Notiz",
  task: "Aufgabe",
};

function formatIsoDay(iso) {
  if (!iso) return "";
  try {
    return deDateLong(iso);
  } catch { return iso; }
}

/**
 * Modal that shows the merged checklist of one calendar event.
 * - Each row: checkbox, label, camera-icon (upload photo), remove-icon.
 * - Bottom: add-item input.
 * - Auto-closes-signal via `onCompleted` when all items are ticked (parent may
 *   refresh the calendar).
 * Props:
 *   eventId — the manual event id (without the "event-" prefix)
 *   open    — boolean
 *   onClose — () => void
 *   onChanged — () => void  called after any successful mutation
 */
export default function EventChecklistDialog({ eventId, open, onClose, onChanged }) {
  // Feb 2026 · User-Directive „Zuweisungen sind Admin-only":
  // Concierges sehen den Zuständigen read-only, dürfen aber nicht selbst
  // umverteilen. Truth-Source ist die reale Rolle des Users.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(false);
  const [busyItem, setBusyItem] = useState(null); // item id under mutation
  const [data, setData] = useState(null); // full checklist payload
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null); // item id whose file input is uploading
  const cameraRefs = useRef({});  // { [itemId]: input } — with capture="environment"
  const galleryRefs = useRef({}); // { [itemId]: input } — without capture (gallery/file picker)

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/calendar/events/${eventId}/checklist`);
      setData(data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      onCloseRef.current?.();
    } finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => {
    if (open && eventId) load();
  }, [open, eventId, load]);

  if (!open) return null;

  const toggleItem = async (item, photoFileId = null) => {
    setBusyItem(item.id);
    try {
      const nextDone = !item.done;
      const { data: fresh } = await api.patch(
        `/calendar/events/${eventId}/checklist/item`,
        { item_id: item.id, done: nextDone, photo_file_id: photoFileId },
      );
      setData(fresh);
      onChanged?.();
      if (fresh?.progress?.total > 0 && fresh.progress.done === fresh.progress.total) {
        toast.success("Alle Punkte erledigt · Termin automatisch abgeschlossen.");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyItem(null); }
  };

  const pickCamera = (item) => {
    cameraRefs.current[item.id]?.click();
  };
  const pickGallery = (item) => {
    galleryRefs.current[item.id]?.click();
  };

  const onFileChosen = async (e, item) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setUploadingFor(item.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Automatically tick the item as done as well when photo lands
      const { data: fresh } = await api.patch(
        `/calendar/events/${eventId}/checklist/item`,
        { item_id: item.id, done: true, photo_file_id: up.id },
      );
      setData(fresh);
      onChanged?.();
      toast.success("Foto gespeichert · Punkt erledigt · Wochenbericht aktualisiert.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setUploadingFor(null); }
  };

  const removeItem = async (item) => {
    if (item.done) return;
    setBusyItem(item.id);
    try {
      const { data: fresh } = await api.delete(`/calendar/events/${eventId}/checklist/item/${item.id}`);
      setData(fresh);
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyItem(null); }
  };

  const addItem = async (e) => {
    e?.preventDefault();
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    try {
      const { data: fresh } = await api.post(`/calendar/events/${eventId}/checklist/item`, { label });
      setData(fresh);
      onChanged?.();
      setNewLabel("");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setAdding(false); }
  };

  const event = data?.event || {};
  const items = data?.items || [];
  const progress = data?.progress || { done: 0, total: 0 };
  const teamMembers = data?.team_members || [];
  const kindLabel = KIND_LABEL[event.kind] || event.kind || "Termin";
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  const setAssignee = async (assignee_id) => {
    try {
      await api.patch(`/calendar/events/${eventId}/assignee`, { assignee_id: assignee_id || null });
      await load();
      onChanged?.();
      toast.success(assignee_id ? "Zuständigen gesetzt." : "Zuweisung entfernt.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.72)}` }}
      data-testid="event-checklist-dialog"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-ivory w-full max-w-2xl max-h-[90vh] flex flex-col border border-gold/50 shadow-2xl"
        style={{ background: BRAND_COLORS.ivory }}
      >
        {/* Header */}
        <div className="p-6 border-b border-navy/10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>{kindLabel}</div>
              <h2 className="font-display text-2xl md:text-3xl text-navy leading-tight" data-testid="checklist-event-title">
                {event.title || "Termin"}
              </h2>
              <div className="text-[12.5px] text-navy-dark/60 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {event.start_date && <span>{formatIsoDay(event.start_date)}</span>}
                {event.customer_name && <span>· {event.customer_name}</span>}
                {event.property_name && <span>· {event.property_name}</span>}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="checklist-close-btn"
              className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy shrink-0"
            >
              Schließen ×
            </button>
          </div>

          {/* Progress */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11.5px] tracking-widest uppercase text-navy-dark/60 mb-1.5">
              <span data-testid="checklist-progress-label">
                {progress.done} von {progress.total} erledigt
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}>
              <div
                className="h-full transition-all"
                style={{ width: `${pct}%`, background: pct === 100 ? STATUS_COLORS.green : BRAND_COLORS.gold }}
                data-testid="checklist-progress-bar"
              />
            </div>
          </div>

          {/* Assignee row */}
          <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="checklist-assignee-row">
            {event.assignee ? (
              <div className="flex items-center gap-2 border border-gold/40 bg-ivory/60 px-3 py-1.5">
                <Avatar name={event.assignee.name} fileId={event.assignee.avatar_file_id} size={26} />
                <div className="text-[12px] text-navy">
                  <span className="text-navy-dark/60">Zuständig: </span>
                  <span className="font-medium">{event.assignee.name}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[12px] text-navy-dark/60">
                <UserCircle2 className="w-4 h-4" strokeWidth={1.6} />
                Noch niemand zuständig
              </div>
            )}
            {isAdmin && (
              <select
                value={event.assignee_id || ""}
                onChange={(e) => setAssignee(e.target.value || null)}
                data-testid="checklist-assignee-select"
                className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-1.5 text-[12px] text-navy-dark"
              >
                <option value="">— zuweisen an —</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} · {m.role}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center text-navy-dark/60 py-10 label-caps flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Wird geladen …
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8" data-testid="checklist-empty">
              <div className="font-display text-xl text-navy mb-1">Keine offenen Punkte</div>
              <div className="text-[13px] text-navy-dark/60">
                Fügen Sie unten manuell einen Punkt hinzu.
              </div>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="checklist-items">
              {items.map((it) => {
                const busy = busyItem === it.id || uploadingFor === it.id;
                return (
                  <li
                    key={it.id}
                    data-testid={`checklist-item-${it.id}${it.done ? "-done" : ""}`}
                    className="bg-white border border-navy/10 p-3 md:p-4 flex items-start gap-3"
                    style={it.done ? { background: withAlpha(STATUS_COLORS.green, 0.06), borderColor: withAlpha(STATUS_COLORS.green, 0.25) } : {}}
                  >
                    {/* Checkbox */}
                    <button
                      type="button"
                      onClick={() => toggleItem(it)}
                      disabled={busy}
                      data-testid={`checklist-toggle-${it.id}`}
                      className="shrink-0 mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors disabled:opacity-50"
                      style={it.done
                        ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
                        : { borderColor: withAlpha(BRAND_COLORS.navy, 0.35), background: "#fff" }}
                      aria-pressed={it.done}
                      title={it.done ? "Als offen markieren" : "Als erledigt markieren"}
                    >
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: it.done ? BRAND_COLORS.ivory : BRAND_COLORS.navy }} />
                        : it.done && <Check className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />}
                    </button>

                    {/* Label + meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="label-caps"
                          style={{ color: it.type === "custom" ? BRAND_COLORS.navy : BRAND_COLORS.gold }}
                        >
                          {it.source_label}
                        </span>
                        {it.created_by_team && (
                          <span
                            className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full border"
                            style={{ color: BRAND_COLORS.goldDark, borderColor: withAlpha(BRAND_COLORS.gold, 0.5), background: withAlpha(BRAND_COLORS.gold, 0.14) }}
                            data-testid={`checklist-created-by-team-${it.id}`}
                            title="Vom Team im Auftrag des Kunden angelegt"
                          >
                            von Weissblom
                          </span>
                        )}
                        {it.priority && it.priority !== "normal" && (
                          <span
                            className="label-caps"
                            style={{ color: it.priority === "urgent" ? STATUS_COLORS.red : BRAND_COLORS.gold }}
                          >
                            · {it.priority === "urgent" ? "Dringend" : "Hoch"}
                          </span>
                        )}
                      </div>
                      <div
                        className={`text-[14px] leading-snug mt-0.5 ${it.done ? "text-navy-dark/60 line-through decoration-navy-dark/25" : "text-navy"}`}
                      >
                        {it.label}
                      </div>
                      {it.description && (
                        <div className={`text-[12px] mt-1 leading-relaxed ${it.done ? "text-navy-dark/45" : "text-navy-dark/65"}`}>
                          {it.description}
                        </div>
                      )}
                      {it.photo_file_id && (
                        <a
                          href={fileUrl(it.photo_file_id)}
                          target="_blank" rel="noreferrer"
                          data-testid={`checklist-photo-${it.id}`}
                          className="inline-flex items-center gap-1.5 mt-2 text-[11px] tracking-wider uppercase text-navy-dark/70 hover:text-navy"
                        >
                          <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
                          Foto anzeigen
                        </a>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Hidden inputs — one for direct-camera, one for gallery/file-picker */}
                      <input
                        ref={(el) => { cameraRefs.current[it.id] = el; }}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => onFileChosen(e, it)}
                        className="hidden"
                        data-testid={`checklist-camera-input-${it.id}`}
                      />
                      <input
                        ref={(el) => { galleryRefs.current[it.id] = el; }}
                        type="file"
                        accept="image/*"
                        onChange={(e) => onFileChosen(e, it)}
                        className="hidden"
                        data-testid={`checklist-gallery-input-${it.id}`}
                      />
                      {/* Kamera (Mobile: öffnet Kamera-App direkt via `capture`) */}
                      <button
                        type="button"
                        onClick={() => pickCamera(it)}
                        disabled={busy}
                        data-testid={`checklist-camera-btn-${it.id}`}
                        title={it.photo_file_id ? "Foto neu aufnehmen" : "Foto aufnehmen (Kamera)"}
                        aria-label="Kamera öffnen"
                        className="w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-gold transition-colors disabled:opacity-50"
                        style={it.photo_file_id ? { background: withAlpha(BRAND_COLORS.gold, 0.15), borderColor: withAlpha(BRAND_COLORS.gold, 0.5) } : {}}
                      >
                        {uploadingFor === it.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin text-navy" />
                          : <Camera className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color: it.photo_file_id ? BRAND_COLORS.gold : BRAND_COLORS.navy }} />}
                      </button>
                      {/* Datei/Galerie */}
                      <button
                        type="button"
                        onClick={() => pickGallery(it)}
                        disabled={busy}
                        data-testid={`checklist-gallery-btn-${it.id}`}
                        title="Aus Galerie / Datei wählen"
                        aria-label="Aus Galerie auswählen"
                        className="w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-gold transition-colors disabled:opacity-50"
                      >
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.6} />
                      </button>
                      {!it.done && (
                        <button
                          type="button"
                          onClick={() => removeItem(it)}
                          disabled={busy}
                          data-testid={`checklist-remove-${it.id}`}
                          title={it.type === "custom" ? "Punkt löschen" : "Aus Termin entfernen"}
                          className="w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-red-700 hover:text-red-700 transition-colors disabled:opacity-50"
                        >
                          {it.type === "custom"
                            ? <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                            : <X className="w-3.5 h-3.5" strokeWidth={1.6} />}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Add item */}
        <form
          onSubmit={addItem}
          className="p-4 md:p-5 border-t border-navy/10"
          data-testid="checklist-add-form"
        >
          <div className="flex items-center gap-2">
            <input
              data-testid="checklist-add-input"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={"Aufgabe hinzufügen (z. B. \u201EFensterputzer bestellen\u201C) \u2026"}
              disabled={adding}
              className="flex-1 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark text-[13px]"
            />
            <button
              type="submit"
              disabled={adding || !newLabel.trim()}
              data-testid="checklist-add-btn"
              className="inline-flex items-center gap-1.5 bg-navy text-ivory px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2} />}
              Hinzufügen
            </button>
          </div>
          {event.customer_id && (
            <div className="mt-2 text-[11px] text-navy-dark/55 flex items-center gap-1.5">
              <UserCircle2 className="w-3 h-3 shrink-0" strokeWidth={1.6} style={{ color: BRAND_COLORS.gold }} />
              Wird auch in der Kunden-App sichtbar („Ihre Anfragen auf einen Blick").
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
